/*
 * MentorModalContext.tsx
 * Provides context and state management for mentor modals in the application.
 * Includes actions for opening/closing modals and utility functions.
 */
import { createContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mentor, mentors } from '../data/mentors';
import { MentorModalContent } from '../components/MentorModalContent';
import { EXPERTISE_COLORS } from '../constants/expertise';

// Helper function to map an expertise to its base color string
function getExpertiseColor(expertise: string): string {
  const mapping: Record<string, string> = {
    'Business & Strategy': 'blue',
    'Game Design & Creative': 'purple',
    'Technology & Development': 'cyan',
    'Marketing & Community': 'green',
    'Production & Management': 'orange',
    'Legal & IP': 'red',
  };
  return mapping[expertise] || 'gray';
}

// Function to generate a random pattern of expertise words for a mentor
const generatePatternForMentor = (mentorExpertise: string[] = [], mentorId?: string) => {
  // Create a deterministic seed based on mentor ID and word index
  const getRandomFromId = (id: string | undefined, index: number) => {
    if (!id) return Math.random();
    const charCodes = id.split('').map((char) => char.charCodeAt(0));
    const seed = charCodes.reduce((acc, code) => acc + code, 0) + index;
    return (seed % 100) / 100;
  };

  return (
    <div className="absolute inset-0 top-[28px] z-10 flex h-fit flex-col items-center justify-center gap-x-0 gap-y-1.5 overflow-hidden px-4 py-3 pl-36 pr-8 sm:top-0 sm:h-full sm:items-end sm:justify-center sm:gap-x-3 sm:gap-y-2 sm:px-4 sm:pl-44 sm:pr-32 sm:pt-0">
      {mentorExpertise.map((word, index) => {
        const randomValue = getRandomFromId(mentorId, index);
        const randomRotate = randomValue > 0.5 ? 'rotate-1' : '-rotate-1';
        const colorClass =
          EXPERTISE_COLORS[word as keyof typeof EXPERTISE_COLORS]?.bg || 'bg-gray-300/30';
        const textClass =
          EXPERTISE_COLORS[word as keyof typeof EXPERTISE_COLORS]?.text || 'text-gray-500';

        return (
          <span
            key={`${word}-${index}`}
            className={`whitespace-nowrap rounded-full text-[10px] font-medium ${randomRotate} px-2 py-0.5 sm:px-4 sm:py-1.5 sm:text-sm ${colorClass} ${textClass} w-fit`}
          >
            {word}
          </span>
        );
      })}
    </div>
  );
};

interface MentorModalContextType {
  openMentorModal: (mentor: Mentor) => void;
  closeMentorModal: () => void;
}

export const MentorModalContext = createContext<MentorModalContextType | undefined>(undefined);

export function MentorModalProvider({ children }: { children: ReactNode }) {
  const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [ignoreUrlChange, setIgnoreUrlChange] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedMentorRef = useRef<Mentor | null>(null);

  const openMentorModal = useCallback(
    (mentor: Mentor) => {
      if (!mentor.id) return;

      setSelectedMentor(mentor);
      selectedMentorRef.current = mentor;
      setIgnoreUrlChange(false);

      // Update URL when modal is opened, preserving the current path
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('mentorId', mentor.id as string);
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      navigate(newUrl, { replace: true });

      // Small delay to ensure the element is in the DOM before starting the animation
      requestAnimationFrame(() => {
        setIsClosing(false);
      });
    },
    [location.pathname, location.search, navigate]
  );

  const closeMentorModal = () => {
    setIsClosing(true);
    setIgnoreUrlChange(true);

    // Remove mentorId from URL but keep other parameters and current path
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('mentorId');
    const newSearch = searchParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    navigate(newUrl, { replace: true });

    // Wait for animation to complete before removing the mentor
    setTimeout(() => {
      setSelectedMentor(null);
      selectedMentorRef.current = null;
      setIsClosing(false);
      setIgnoreUrlChange(false); // Reset flag to allow future modal opens
    }, 500);
  };

  // Add effect to update iOS browser color
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }

    if (selectedMentor?.expertise?.[0]) {
      const baseColor = getExpertiseColor(selectedMentor.expertise[0]);
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', `var(--${baseColor}-50)`);
    } else {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#ffffff');
    }

    return () => {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#ffffff');
    };
  }, [selectedMentor]);

  // Handle URL changes to open mentor modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mentorId = searchParams.get('mentorId');

    if (mentorId && !selectedMentorRef.current && !ignoreUrlChange) {
      // Use the statically imported mentors instead of dynamic import
      const mentorFromUrl = mentors.find((m) => m.id === mentorId);
      if (mentorFromUrl) {
        setSelectedMentor(mentorFromUrl);
        selectedMentorRef.current = mentorFromUrl;
        setIgnoreUrlChange(false);
        // Small delay to ensure the element is in the DOM before starting the animation
        requestAnimationFrame(() => {
          setIsClosing(false);
        });
      }
    }
  }, [location.search, ignoreUrlChange]);

  return (
    <MentorModalContext.Provider value={{ openMentorModal, closeMentorModal }}>
      {children}
      {(selectedMentor || isClosing) && (
        <div
          className={`fixed inset-0 z-[9999] transition-all duration-500 ease-in-out ${
            isClosing || !selectedMentor
              ? 'bg-background/0 opacity-0 backdrop-blur-none'
              : 'bg-background/50 opacity-100 backdrop-blur'
          }`}
          style={{
            backgroundColor: selectedMentor
              ? `color-mix(in srgb, var(--${getExpertiseColor(selectedMentor.expertise?.[0] || '')}-50) 15%, transparent)`
              : undefined,
          }}
          onClick={closeMentorModal}
        >
          <div className="fixed inset-0 flex items-center justify-center sm:p-4">
            <div className="absolute inset-0" onClick={closeMentorModal}></div>
            {selectedMentor && (
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
                className={`relative size-full overflow-hidden border-2 bg-white transition-all duration-500 ease-in-out sm:h-fit sm:max-w-5xl sm:rounded-2xl md:h-[90vh] xl:aspect-[4/3] xl:h-auto ${
                  isClosing ? 'animate-modal-close' : 'animate-modal-open'
                } shadow-[0_0_40px_-4px_rgba(0,0,0,0.15)] xl:w-[75vw]`}
                style={{
                  maxHeight:
                    window.innerWidth >= 1280
                      ? 'none'
                      : window.innerWidth >= 640
                        ? 'calc(100vh - 2rem)'
                        : undefined,
                  minHeight:
                    window.innerWidth >= 768 && window.innerWidth < 1280 ? '600px' : undefined,
                  borderColor: `var(--${getExpertiseColor(selectedMentor.expertise?.[0] || '')}-300)`,
                  borderWidth: window.innerWidth >= 640 ? '0px' : '0px',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button - Sticky on mobile */}
                <button
                  onClick={closeMentorModal}
                  className="fixed right-4 top-4 z-[10000] rounded-full bg-white p-2 text-gray-600 shadow-md transition-colors duration-200 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:absolute"
                  aria-label="Close modal"
                >
                  <svg className="size-6" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    />
                  </svg>
                </button>

                {/* Modal Content - Scrollable Container */}
                <div className="h-full overflow-y-auto">
                  {/* Modal Header - Fixed on desktop, scrollable on mobile */}
                  <div className="relative h-36 sm:-mb-16 sm:h-48">
                    {/* Background layers */}
                    <div className="absolute inset-0 bg-gray-100 sm:rounded-t-2xl" />

                    {/* Word cloud background */}
                    {(() => {
                      const firstExpertise =
                        selectedMentor.expertise && selectedMentor.expertise.length > 0
                          ? selectedMentor.expertise[0]
                          : '';
                      const baseColor = getExpertiseColor(firstExpertise || '');
                      return (
                        <>
                          <div
                            className="absolute inset-0 sm:rounded-t-2xl"
                            style={{ backgroundColor: `var(--${baseColor}-50)` }}
                          />

                          {/* Profile picture */}
                          <div className="absolute left-4 top-8 z-40 sm:bottom-0 sm:left-8 sm:top-8">
                            <div
                              className="size-24 overflow-hidden rounded-full border-4 bg-white shadow-lg sm:size-32"
                              style={{ borderColor: `var(--${baseColor}-300)` }}
                            >
                              <img
                                src={selectedMentor.image}
                                alt={selectedMentor.name}
                                className="size-full object-cover"
                              />
                            </div>
                          </div>
                        </>
                      );
                    })()}

                    {/* Word cloud pattern */}
                    <div className="absolute inset-0 z-20 py-2 pl-44 pr-20">
                      {selectedMentor.id &&
                        generatePatternForMentor(selectedMentor.expertise || [], selectedMentor.id)}
                    </div>

                    {/* Gradient overlay */}
                    <div
                      className="absolute inset-0 z-30 sm:rounded-t-2xl"
                      style={{
                        background:
                          'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.95) 80%, white 100%)',
                      }}
                    />
                  </div>

                  {/* Modal Content */}
                  <div className="relative z-40 px-4 sm:px-8 sm:pt-8">
                    <MentorModalContent mentor={selectedMentor} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </MentorModalContext.Provider>
  );
}
